/**
 * 摩根合规受限用户「无操作」安全踢出。
 *
 * 需求背景：合规受限用户（后端 /json/user/userdetail 返回 complianceRestricted=1）登录后，
 * 若空闲超过阈值（生产 30 分钟 / 测试 5 分钟），再次打开/使用（切回标签页、页面重载、路由跳转、任意交互）
 * 时应令登录态失效并提示后踢出到登录页，需重新登录。
 *
 * 是否为合规受限用户以后端接口字段 complianceRestricted 为准（不再依赖前端写死的组织 ID）。
 * 该字段只在登录时查询一次并持久化，之后刷新 / 切页直接读缓存，不再重复请求用户详情接口。
 * 超时踢出时调用 /json/user/compliance-logout 通知后端合规登出，提示「登录状态已失效」后清理本地会话并跳转登录。
 *
 * 触发方式为双重保障：
 * 1. 后台定时器周期性校验空闲时长（覆盖「一直挂着不动」的场景）；
 * 2. 用户活动 / 可见性变化 / 路由跳转时即时校验（覆盖「再次打开即失效」的场景）。
 *
 * 仅对「已登录 + complianceRestricted=1」的用户生效，其余用户零影响。
 * 录会 / 录音中（录音面板已打开 store.state.isRecording，或录音组件通过 useIdleExemptTask
 * 登记的真实采音 / 推流状态）或音视频播放中（含纪要音频）不做检测与踢出，
 * 避免打断正在进行的录制或播放。
 *
 * 模块内使用单例状态：全应用只有一个根组件调用 useIdleSessionTimeout，
 * 路由守卫复用同一份 lastActive 判定逻辑。
 */

import { onMounted, onBeforeUnmount, watch, getCurrentInstance } from 'vue';
import { ElMessageBox } from 'element-plus';
import router from '@/router';
import store from '@/store';
import electron, { isElectron } from '@/utils/electron';
import { isOverseasEnv } from '@/utils/api';
import slsLogger from '@/utils/slsLogger';
import { emitter } from '@/plugins/emitter';
import { checkLogin, getStorageAuthInfo, storage } from '@/utils/storageOperateApi';
import { LOCAL_STORAGE_KEY } from '@/constants';
import { getUserDetail, complianceLogout } from '@/jmHttp/api/common';
import { isBuild } from '@/jmHttp/host';
import i18n from '@/plugins/i18n';

/** 无操作上限：生产 30 分钟，测试 5 分钟（便于联调验证） */
const IDLE_LIMIT_MS = (isBuild ? 30 : 5) * 60 * 1000;
/** 后台定时校验间隔：30 秒 */
const CHECK_INTERVAL_MS = 30 * 1000;
/** 用户活动事件处理节流间隔，避免 mousemove 等高频事件频繁读写 */
const ACTIVITY_THROTTLE_MS = 1000;
/** 研究模式文档分享页：停留期间不触发合规空闲踢出 */
const IDLE_EXEMPT_ROUTE = '/research/document/share';
/**
 * 最近活动时间戳持久化 key。
 * 统一走项目的 storage 封装（@/utils/storageOperateApi），与鉴权信息等业务数据一致：
 * 按 SAVE_STORAGE_TYPE 自动选择 local/sessionStorage，并在退出登录时随会话一并清理。
 */
const LAST_ACTIVE_KEY = LOCAL_STORAGE_KEY.COMPLIANCE_IDLE_LAST_ACTIVE;
/**
 * 合规受限标记持久化 key。登录时查询一次后写入，页面刷新 / 切页直接复用，
 * 与 lastActive 一样随退出登录清理，避免跨账号复用上一个用户的结果。
 */
const COMPLIANCE_RESTRICTED_KEY = LOCAL_STORAGE_KEY.COMPLIANCE_RESTRICTED;
/** 需要监听的用户活动事件 */
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click', 'wheel'];

/** 踢出流程防重入标记 */
let isLoggingOut = false;
/** 后台定时器句柄 */
let checkTimerId = null;
/** 活动事件节流的上次执行时间 */
let activityThrottleTs = 0;
/**
 * 当前用户是否为合规受限用户（后端 complianceRestricted=1）。
 * 初始为 false，接口返回前不介入；查询成功后据此启用/关闭守卫。
 */
let isComplianceRestricted = false;
/**
 * 正在进行中的合规查询 Promise。无感登录（router.beforeEach）与首屏恢复可能几乎同时发生，
 * 复用同一个请求可避免一次登录重复调用用户详情接口。
 */
let pendingComplianceProbe = null;
/**
 * 业务侧登记的「进行中」任务：key 为登记时分配的唯一 id，value 为任务名（仅用于日志排查）。
 * 用于录音真实采音状态这类只存在于组件内部、没有全局状态可读的场景。
 */
const activeExemptTasks = new Map();
/** 豁免任务 id 自增种子，保证同名任务的多个组件实例互不覆盖 */
let exemptTaskSeed = 0;

/**
 * 获取路由 path。路由对象通常同时提供 path / fullPath，测试或外部调用也可能直接传字符串。
 * query / hash 不参与匹配，确保分享链接携带参数时仍然命中豁免路由。
 * @param {object|string|null|undefined} route - Vue Router 路由对象或路径
 * @returns {string} 不含 query / hash 的路径
 */
function getRoutePath(route) {
  const routePath = typeof route === 'string' ? route : route?.path || route?.fullPath;
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
  const pathWithQueryOrHash = String(routePath || currentPath);
  // `?` / `#` 后分别是 query / hash，不参与路由豁免匹配。
  const pathWithoutQuery = pathWithQueryOrHash.split('?')[0];
  const pathWithoutHash = pathWithoutQuery.split('#')[0];
  // 兼容分享链接末尾带一个 `/` 的写法。
  return pathWithoutHash.endsWith('/') ? pathWithoutHash.slice(0, -1) : pathWithoutHash;
}

/**
 * 是否为合规空闲踢出豁免路由。
 * @param {object|string|null|undefined} route - Vue Router 路由对象或路径
 * @returns {boolean} true 表示当前 / 目标路由不做合规空闲踢出
 */
function isIdleExemptRoute(route) {
  return getRoutePath(route) === IDLE_EXEMPT_ROUTE;
}

/**
 * 获取当前路由。router.beforeEach 执行时 currentRoute 仍是跳转前路由，目标路由由调用方显式传入。
 * @returns {object|string|null|undefined} 当前路由
 */
function getCurrentRoute() {
  return router.currentRoute?.value;
}

/**
 * 判定当前是否需要启用空闲踢出。
 * @returns {boolean} true 表示当前用户命中「已登录 + 合规受限用户」
 */
function shouldGuard(route = getCurrentRoute()) {
  // 分享页即使用户长时间无操作，也不能触发合规空闲踢出
  if (isIdleExemptRoute(route)) return false;
  // 未登录不介入
  if (!checkLogin()) return false;
  // 仅合规受限用户（以后端 complianceRestricted 字段为准）
  if (!isComplianceRestricted) return false;
  return true;
}

/**
 * 读取已持久化的合规受限标记。
 * @returns {boolean|null} 布尔值为已查询过的结果；null 表示本次登录尚无记录，需要查一次接口
 */
function readStoredComplianceFlag() {
  const value = storage.get(COMPLIANCE_RESTRICTED_KEY);
  // 仅布尔值视为有效记录，其余（无记录 / 脏数据）都按「未查询过」处理
  return typeof value === 'boolean' ? value : null;
}

/**
 * 持久化合规受限标记，供本次登录后的刷新 / 切页复用。
 * @param {boolean} restricted - 是否为合规受限用户
 * @returns {void}
 */
function writeStoredComplianceFlag(restricted) {
  storage.set(COMPLIANCE_RESTRICTED_KEY, restricted);
}

/**
 * 清除已持久化的合规受限标记。
 * @returns {void}
 */
function clearStoredComplianceFlag() {
  storage.remove(COMPLIANCE_RESTRICTED_KEY);
}

/**
 * 登录时拉取用户详情并更新、持久化合规受限标记；命中受限用户时随即执行一次初始化守卫。
 * 查询失败保持保守（不启用守卫且不落缓存），避免因接口异常误踢正常用户，下次进入会重新查询。
 *
 * 仅在登录链路调用（会话内登录成功事件、Electron / URL 无感登录），
 * 页面刷新与切页由 restoreComplianceFlag 读缓存恢复，不再请求接口。
 *
 * token 处理对齐 json_author_query-custom-menu（fetchUserMenuAction）：
 * 优先使用调用方显式传入的本次登录 token/uid 写入 headers，
 * 避免 Electron 冷启动时读到 storage 中残留的旧 webtoken。
 *
 * @param {{ token?: string, uid?: string|number }} [auth] - 可选，本次登录鉴权（Electron 无感登录传入）
 * @returns {Promise<void>}
 */
export async function refreshComplianceFlag(auth = {}) {
  // 同一次登录内复用在途请求，避免无感登录与首屏恢复各发一次
  if (pendingComplianceProbe) return pendingComplianceProbe;
  pendingComplianceProbe = probeComplianceFlag(auth).finally(() => {
    pendingComplianceProbe = null;
  });
  return pendingComplianceProbe;
}

/**
 * 实际执行合规受限状态查询与落库，由 refreshComplianceFlag 负责并发去重。
 * @param {{ token?: string, uid?: string|number }} auth - 本次登录鉴权
 * @returns {Promise<void>}
 */
async function probeComplianceFlag(auth) {
  const jmAuthInfo = getStorageAuthInfo(LOCAL_STORAGE_KEY.JM_AUTH_INFO) || {};
  // 与 custom-menu 一致：显式 token 优先，其次才回退 storage
  const token = auth.token || jmAuthInfo.webtoken || jmAuthInfo.token || '';
  const uid = auth.uid || jmAuthInfo.uid || '';

  // 无显式 token 时仍要求 BRM 已登录；有显式 token（Electron 刚写入鉴权）则可直接探测
  if (!auth.token && !checkLogin()) {
    isComplianceRestricted = false;
    return;
  }
  if (!token) {
    isComplianceRestricted = false;
    return;
  }

  try {
    // hideMessage：合规探测失败不得弹出「登录失效」并踢登；仅影响是否启用空闲守卫
    // headers.token / web_token / uid：与 getUserCustomMenu 登录链路相同，拦截器优先用调用方传入值
    const res = await getUserDetail(
      {},
      {
        hideMessage: true,
        headers: {
          token,
          web_token: token,
          uid
        }
      }
    );
    isComplianceRestricted = Number(res?.data?.complianceRestricted) === 1;
    // 落缓存：本次登录后的刷新 / 切页直接复用该结果，不再请求接口
    writeStoredComplianceFlag(isComplianceRestricted);
  } catch (error) {
    // 接口异常时不启用守卫，避免误踢
    isComplianceRestricted = false;
    slsLogger.console({
      action: 'compliance',
      level: 'error',
      message: '查询用户合规受限状态失败',
      extra: { errorMessage: error?.message || 'Unknown error' }
    });
    return;
  }
  // 拿到结果后按初始化逻辑处理一次（覆盖「再次打开即失效」）
  initGuard();
}

/**
 * 页面加载（刷新 / 切页）时恢复合规受限标记：优先读登录时写下的缓存，不再请求接口。
 * 仅当本次登录尚无缓存记录（登录时查询失败，或登录发生在该缓存上线之前）才补查一次并落库。
 * @returns {Promise<void>}
 */
async function restoreComplianceFlag() {
  // 未登录不介入，也无需查询
  if (!checkLogin()) {
    isComplianceRestricted = false;
    return;
  }
  const stored = readStoredComplianceFlag();
  // 无缓存记录：补查一次（无感登录已在查询中时会复用同一请求）
  if (stored === null) {
    await refreshComplianceFlag();
    return;
  }
  isComplianceRestricted = stored;
  initGuard();
}

/**
 * 读取最近活动时间戳。
 * @returns {number} 毫秒时间戳；无有效记录时返回 0
 */
function readLastActive() {
  const value = Number(storage.get(LAST_ACTIVE_KEY));
  // 只有正数才是有效记录，否则视为无记录
  return Number.isFinite(value) && value > 0 ? value : 0;
}

/**
 * 写入最近活动时间戳。
 * @param {number} ts - 毫秒时间戳
 * @returns {void}
 */
function writeLastActive(ts) {
  storage.set(LAST_ACTIVE_KEY, ts);
}

/**
 * 清除最近活动时间戳。
 * @returns {void}
 */
function clearLastActive() {
  storage.remove(LAST_ACTIVE_KEY);
}

/**
 * 判断是否已超过无操作上限。
 * @returns {boolean} true 表示已空闲超过阈值（生产 30 分钟 / 测试 5 分钟）
 */
function isIdleExpired() {
  const last = readLastActive();
  // 无记录时不算超时（尚未开始计时）
  if (!last) return false;
  return Date.now() - last >= IDLE_LIMIT_MS;
}

/**
 * 停止后台定时器。
 * @returns {void}
 */
function stopCheckTimer() {
  // 已存在定时器时才清理，避免重复清空
  if (checkTimerId) {
    clearInterval(checkTimerId);
    checkTimerId = null;
  }
}

/**
 * 是否处于录会 / 录音中状态。录制期间不做空闲踢出，避免打断正在进行的录制。
 * store.state.isRecording 语义为「录音面板已打开」，真正的采音 / 推流状态由录音组件通过
 * useIdleExemptTask 登记，两者取并集以覆盖「面板已收起但仍在录」的情况。
 * @returns {boolean} true 表示当前正在录会 / 录音
 */
function isRecordingActive() {
  return !!store.state.isRecording || activeExemptTasks.size > 0;
}

/**
 * 登记一个「进行中即不踢出」的任务，任务存续期间豁免空闲踢出。
 * 供录音等没有全局状态、只能由业务组件自行上报进行中状态的场景使用。
 * @param {string} name - 任务名，仅用于排查（如 'recorder'、'recorder-shared'）
 * @param {import('vue').Ref<boolean>|(() => boolean)} isActive - 任务是否进行中
 * @returns {void}
 * @example
 * useIdleExemptTask('recorder', () => isRecording.value || tingwuIsRecording.value);
 */
export function useIdleExemptTask(name, isActive) {
  exemptTaskSeed += 1;
  const taskId = `${name}#${exemptTaskSeed}`;
  const release = () => activeExemptTasks.delete(taskId);

  watch(
    isActive,
    (active) => {
      // 任务开始时登记豁免，结束后立即撤销，避免任务结束仍长期不踢出
      if (active) {
        activeExemptTasks.set(taskId, name);
      } else {
        release();
      }
    },
    { immediate: true }
  );

  // 组件卸载兜底：任务未正常结束就被销毁时也要撤销登记
  if (getCurrentInstance()) {
    onBeforeUnmount(release);
  }
}

/**
 * 是否有音视频正在播放（含纪要音频）。
 * 纪要等播放器为局部状态、无全局 store，这里通过检测页面上真实的 audio/video 元素是否处于
 * 播放中（未暂停、未结束、已有可播放数据）来兜底；同时叠加全局音频条 store 的播放标记。
 * @returns {boolean} true 表示当前有媒体正在播放
 */
function isMediaPlaying() {
  // 全局音频条（daily 播报等）显式播放标记
  if (store.state.globalAudioPlayback?.playing) return true;
  // 页面上任一真实媒体元素处于播放中（覆盖纪要阿里云播放器等局部播放器）
  const mediaElements = document.querySelectorAll('audio, video');
  for (let i = 0; i < mediaElements.length; i++) {
    const el = mediaElements[i];
    // 未暂停 + 未结束 + 已有当前帧数据，视为正在播放
    if (!el.paused && !el.ended && el.readyState >= 2) return true;
  }
  return false;
}

/**
 * 是否处于「录制中或媒体播放中」的豁免状态。命中则不做空闲踢出，避免打断录制或纪要音频播放。
 * @returns {boolean} true 表示当前应豁免空闲踢出
 */
function isIdleKickExempt() {
  return isRecordingActive() || isMediaPlaying();
}

/**
 * 执行踢出：清理计时、记录审计日志，提示登录失效后退出并跳转登录页。
 * @returns {Promise<void>}
 */
async function performIdleLogout() {
  // 防止定时器 / 活动 / 路由多路径重复触发踢出
  if (isLoggingOut) return;
  // 录制中 / 媒体播放中（含纪要音频）：不踢出，并视为活跃刷新计时，避免结束瞬间因时间戳陈旧被误踢
  if (isIdleKickExempt()) {
    writeLastActive(Date.now());
    return;
  }
  isLoggingOut = true;

  clearLastActive();
  stopCheckTimer();

  slsLogger.console({
    action: 'logout',
    message: `合规受限用户 ${isBuild ? 30 : 5} 分钟无操作，登录态失效自动踢出`
  });

  // 先通知后端合规超时登出（使服务端会话失效）；失败也继续本地登出
  try {
    await complianceLogout({}, { hideMessage: true });
  } catch (error) {
    slsLogger.console({
      action: 'logout',
      level: 'error',
      message: '合规超时登出接口调用失败',
      extra: { errorMessage: error?.message || 'Unknown error' }
    });
  }

  // 与 token 失效提示一致：先弹窗，确认后再清理会话并跳登录页
  ElMessageBox.alert(i18n.global.t('common.tip.login_expired'), i18n.global.t('common.tip.tips'), {
    customClass: 'w480 message-box-normal',
    confirmButtonText: i18n.global.t('common.button.confirm'),
    callback: () => {
      finishIdleLogout();
    }
  });
}

/**
 * 提示确认后执行实际退出与跳转。
 * @returns {Promise<void>}
 */
async function finishIdleLogout() {
  // Electron 客户端走客户端自身退出逻辑
  if (isElectron) {
    electron.loginOut();
    return;
  }

  try {
    await store.dispatch('logoutAction');
  } catch (error) {
    // 退出接口异常时兜底清理本地会话，确保前端登录态被终止
    slsLogger.console({
      action: 'logout',
      level: 'error',
      message: '空闲踢出退出登录异常，执行本地兜底清理',
      extra: { errorMessage: error?.message || 'Unknown error' }
    });
    try {
      storage.clear();
      store.commit('clearState');
      store.commit('jm/clearState');
    } catch (clearError) {
      slsLogger.console({
        action: 'logout',
        level: 'error',
        message: '本地兜底清理异常',
        extra: { errorMessage: clearError?.message || 'Unknown error' }
      });
    }
  } finally {
    redirectToLogin();
  }
}

/**
 * 跳转登录页（海外环境跳海外登录地址）。
 * @returns {void}
 */
function redirectToLogin() {
  // 海外环境跳转独立的海外登录页
  if (isOverseasEnv()) {
    location.href = ENV_DATA.VITE_OVERSEAS_LOGIN_URL;
    return;
  }
  router.push({ name: 'login' });
}

/**
 * 校验空闲状态，超时则踢出。用于定时器、可见性变化、路由守卫等「仅校验不刷新」的场景。
 * @returns {boolean} true 表示已触发踢出（或正在踢出）
 */
function checkIdleAndKick(targetRoute = null) {
  // 已在踢出流程中，直接视为拦截
  if (isLoggingOut) return true;
  // 目标是分享页时放行导航；不能只看 currentRoute，否则从其它页面进入分享页前会先被旧页面的过期时间拦截
  if (targetRoute && isIdleExemptRoute(targetRoute)) return false;

  const currentRoute = getCurrentRoute();
  // 从分享页离开时，将这次主动导航视为一次活动，避免把进入分享页前的旧时间戳带到普通页面
  if (targetRoute && isIdleExemptRoute(currentRoute)) {
    if (shouldGuard(targetRoute)) {
      writeLastActive(Date.now());
    }
    return false;
  }

  // 不满足守卫条件不处理
  if (!shouldGuard(targetRoute || currentRoute)) return false;
  // 录制中 / 媒体播放中视为活跃：刷新计时且不踢出（不拦截路由导航）
  if (isIdleKickExempt()) {
    writeLastActive(Date.now());
    return false;
  }
  // 超时则踢出
  if (isIdleExpired()) {
    performIdleLogout();
    return true;
  }
  return false;
}

/**
 * 记录一次真实用户活动：先校验是否已超时（覆盖「空闲后再次操作即踢出」），未超时则刷新活动时间。
 * @returns {void}
 */
function recordActivity() {
  // 不满足守卫条件不处理
  if (!shouldGuard()) return;
  // 录制中 / 媒体播放中视为活跃：刷新计时且不踢出
  if (isIdleKickExempt()) {
    writeLastActive(Date.now());
    return;
  }
  // 空闲已超时：本次操作即触发踢出
  if (isIdleExpired()) {
    performIdleLogout();
    return;
  }
  writeLastActive(Date.now());
}

/**
 * 用户活动事件回调（节流），避免高频事件频繁执行守卫判定与读写。
 * @returns {void}
 */
function onUserActivity() {
  const now = Date.now();
  // 节流：间隔不足时跳过
  if (now - activityThrottleTs < ACTIVITY_THROTTLE_MS) return;
  activityThrottleTs = now;
  recordActivity();
}

/**
 * 可见性变化回调：标签页重新可见（再次打开）时仅做校验，不刷新活动时间。
 * @returns {void}
 */
function onVisibilityChange() {
  // 仅在页面重新可见时校验
  if (document.visibilityState === 'visible') {
    checkIdleAndKick();
  }
}

/**
 * 初始化守卫：命中条件且已超时则立即踢出（覆盖「再次打开页面即失效」），
 * 未超时则将打开/重载视为一次使用并以当前时间重新计时。
 * @returns {void}
 */
function initGuard() {
  // 不满足守卫条件不处理
  if (!shouldGuard()) return;
  // 录制中 / 媒体播放中视为活跃：刷新计时且不踢出
  if (isIdleKickExempt()) {
    writeLastActive(Date.now());
    return;
  }
  // 已有历史记录且超时 -> 页面重开即踢出
  if (isIdleExpired()) {
    performIdleLogout();
    return;
  }
  // 未超时：打开/重载页面本身视为一次「使用」，以当前时间重新开始计时
  writeLastActive(Date.now());
}

/**
 * 会话内登录成功后重置状态并重新查询合规标记后启动计时。
 * 先清除上一次的陈旧时间戳与标记，避免复用旧账号数据导致误踢。
 * @returns {void}
 */
function resetOnLogin() {
  isLoggingOut = false;
  isComplianceRestricted = false;
  clearLastActive();
  // 清掉上一个账号的缓存结果与在途请求，避免新账号复用旧标记或旧 token 的查询结果
  clearStoredComplianceFlag();
  pendingComplianceProbe = null;
  // 重新查询新登录用户的合规受限状态；命中则由 refreshComplianceFlag -> initGuard 开始计时
  refreshComplianceFlag();
}

/**
 * 路由跳转时的空闲校验，供 router.beforeEach 调用。
 * 作为「路由跳转 = 使用」的强校验兜底，超时则触发踢出。
 * @returns {boolean} true 表示已触发踢出，调用方应中止本次导航
 */
export function checkIdleSessionOnNavigation(targetRoute) {
  return checkIdleAndKick(targetRoute);
}

/**
 * 空闲踢出组合式函数：在根组件挂载时注册活动监听、可见性监听、后台定时器与登录事件。
 * @returns {void}
 */
export function useIdleSessionTimeout() {
  onMounted(() => {
    // 注册用户活动监听（passive，避免影响滚动性能）
    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, onUserActivity, { passive: true });
    });
    document.addEventListener('visibilitychange', onVisibilityChange);

    // 后台定时校验（覆盖长时间无任何事件的纯挂机场景）
    checkTimerId = setInterval(checkIdleAndKick, CHECK_INTERVAL_MS);

    // 会话内登录成功后重置并重新查询合规标记
    emitter.on('login-success', resetOnLogin);

    // 等首屏路由完成后再恢复标记：Electron 无感登录会在 beforeEach 里写入本次 token 并触发查询，
    // 此处优先复用登录时落下的缓存，无缓存才补查一次
    router.isReady().then(() => {
      restoreComplianceFlag();
    });
  });

  onBeforeUnmount(() => {
    ACTIVITY_EVENTS.forEach((eventName) => {
      window.removeEventListener(eventName, onUserActivity);
    });
    document.removeEventListener('visibilitychange', onVisibilityChange);
    stopCheckTimer();
    emitter.off('login-success', resetOnLogin);
  });
}
